import { useEffect, useState } from 'react'
import { Button } from 'components/common'
import { ExportIcon, PlusIcon } from 'components/icons'
import useModal from 'hooks/useModal'
import CreatePickerMapModal from './createPickerMapModal'
import DataTableImportButton from 'components/common/dataTable/dataTableImportButton'
import { usePageAccess } from 'hooks'
import toast from 'react-hot-toast'
import {
  exportPickerMappingApi,
  importPickerMappingApi,
  syncEmpIdApi,
} from 'utils/api/pickerEmpMapApi'
import { handleError } from 'utils/helpers/handleError'
import { ImportColumn } from 'components/common/importModal'
import { getImportTemplate, downloadImportSample } from 'utils/api/importApi'

const ExportPickerMappingButton = () => {
  const downloadCsv = async () => {
    const loadingToast = toast.loading('Loading..')
    try {
      await exportPickerMappingApi()
      toast.success('File will be sent to you in email!', { id: loadingToast })
    } catch (error) {
      handleError(error, loadingToast)
    }
  }

  return (
    <Button
      type="button"
      buttonText="Export"
      variant="custom"
      iconClassName="!h-5 !w-5 text-dataTableIcon"
      icon={<ExportIcon />}
      className="rounded-lg px-3.5 py-2 text-sm font-semibold text-sidebar-primary hover:bg-gray-200"
      onClick={downloadCsv}
    />
  )
}

const SyncEmpIdButton = () => {
  const [isSyncing, setIsSyncing] = useState(false)

  const handleSyncEmpId = async () => {
    setIsSyncing(true)
    const loadingToast = toast.loading('Initiating Sync Employee ID cron..')
    try {
      await syncEmpIdApi()
      toast.success(
        'Cron trigger successful. You will be notified after completion.',
        { id: loadingToast },
      )
    } catch (error) {
      handleError(error, loadingToast, { default: 'Failed to trigger cron' })
    } finally {
      setIsSyncing(false)
    }
  }

  return (
    <Button
      type="button"
      buttonText="Sync Employee ID"
      variant="outline"
      size="small"
      className="!border !py-1.5 !font-normal"
      onClick={handleSyncEmpId}
      loading={isSyncing}
      disabled={isSyncing}
    />
  )
}

const CreatePickerEmpMap = ({
  refetchMappings,
}: {
  refetchMappings: () => void
}) => {
  const { closeModal, isOpen, openModal } = useModal()
  const { isEditor } = usePageAccess()
  const [pickerImportColumns, setPickerImportColumns] = useState<
    ImportColumn[]
  >([])

  useEffect(() => {
    getImportTemplate('pickerMapping')
      .then((res: { data: { columns: ImportColumn[] } }) =>
        setPickerImportColumns(res.data.columns),
      )
      .catch(() => {})
  }, [])

  const handleDownloadPickerSample = async () => {
    const response = await downloadImportSample('pickerMapping')
    const url = URL.createObjectURL(
      new Blob([response.data], { type: 'text/csv' }),
    )
    const link = document.createElement('a')
    link.href = url
    link.download = 'picker_mapping_sample.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  if (!isEditor) {
    return (
      <div className="flex items-center gap-2">
        <ExportPickerMappingButton />
      </div>
    )
  }

  return (
    <div>
      <CreatePickerMapModal
        closeModal={closeModal}
        isOpen={isOpen}
        onSuccess={async () => {
          refetchMappings()
        }}
      />

      <div className="flex items-center justify-between gap-2">
        <Button
          type="button"
          buttonText="Create Picker Mapping"
          iconClassName="!h-4 !w-4 text-dataTableIcon"
          icon={<PlusIcon />}
          onClick={() => {
            openModal()
          }}
        />

        <div className="flex items-center gap-2">
          <SyncEmpIdButton />
          <DataTableImportButton
            uploadApiFunction={importPickerMappingApi}
            importColumns={pickerImportColumns}
            importModuleName="picker mapping"
            onDownloadSample={handleDownloadPickerSample}
          />
          <ExportPickerMappingButton />
        </div>
      </div>
    </div>
  )
}

export default CreatePickerEmpMap
